import React, { useState, useEffect } from 'react';
import { updateBooking, getFileFromS3 } from '../../api';
import { parseCurrencyValue } from '../../../utils/currency';

const parseDate = (value) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateTime = (value) => {
  const parsed = parseDate(value);
  if (!parsed) {
    return '';
  }

  const pad = (part) => String(part).padStart(2, '0');

  return [
    `${pad(parsed.getDate())}/${pad(parsed.getMonth() + 1)}/${pad(parsed.getFullYear()).slice(-2)}`,
    `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
  ].join(' ');
};

const formatDuration = (milliseconds) => {
  if (!Number.isFinite(milliseconds)) {
    return '';
  }

  const totalMinutes = Math.round(Math.abs(milliseconds) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const getScheduledDate = (booking) => {
  return parseDate(booking?.datetimeAtendimento) || parseDate(booking?.dataAtendimento);
};

function ViewBookingModal({ booking, showModal, onClose, startDate, endDate, onUpdate }) {
  const [editedComission, setEditedComission] = useState(0);
  const [editedValorTotal, setEditedValorTotal] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (booking) {
      setEditedComission(booking.comission || 0);
      setEditedValorTotal(parseFloat(booking.valorTotal) || 0);
    }
  }, [booking]);

  useEffect(() => {
    if (showModal) {
      const handleEscapeKey = (event) => {
        if (event.key === 'Escape') {
          onClose();
        }
      };
      
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [showModal, onClose]);

  const handleUpdateComission = async () => {
    try {
      setIsUpdating(true);
      await updateBooking({
        atendimento_id: booking.atendimento_id,
        comission: parseCurrencyValue(editedComission),
        valorTotal: parseCurrencyValue(editedValorTotal)
      });
      onUpdate(); // Call the update callback
      onClose();
    } catch (error) {
      console.error('Error updating booking:', error);
      alert('Failed to update booking');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDownloadReceipt = async () => {
    try {
      setIsDownloading(true);
      await getFileFromS3(booking.file_key, true);
    } catch (error) {
      console.error('Error downloading receipt:', error);
      alert('Failed to download receipt');
    } finally {
      setIsDownloading(false);
    }
  };

  if (!showModal || !booking) return null;

  const scheduledDate = getScheduledDate(booking);
  const arrivedDate = parseDate(booking.status_chegou_at);
  const finalizedDate = parseDate(booking.status_realizado_at);
  const arrivedTimestamp = formatDateTime(booking.status_chegou_at);
  const finalizedTimestamp = formatDateTime(booking.status_realizado_at);
  const totalBookingTime = arrivedDate && finalizedDate
    ? formatDuration(finalizedDate - arrivedDate)
    : '';
  const arrivalDelta = scheduledDate && arrivedDate
    ? arrivedDate - scheduledDate
    : null;
  const arrivalDeltaText = arrivalDelta !== null && Number.isFinite(arrivalDelta)
    ? formatDuration(arrivalDelta)
    : '';
  const arrivalDeltaLabel = arrivalDelta !== null && arrivalDelta < 0
    ? 'Paciente adiantado em'
    : 'Paciente atrasado em';
  const scheduledDisplay = booking.datetimeAtendimento || booking.dataAtendimento || '';

  if (
    typeof window !== 'undefined'
    && window.localStorage?.getItem('debugBookingTiming') === '1'
  ) {
    console.debug('[ViewBookingModal timing]', {
      atendimento_id: booking.atendimento_id,
      status: booking.status,
      datetimeAtendimento: booking.datetimeAtendimento,
      dataAtendimento: booking.dataAtendimento,
      status_chegou_at: booking.status_chegou_at,
      status_realizado_at: booking.status_realizado_at,
      scheduledDate,
      arrivedDate,
      finalizedDate,
      arrivalDeltaMinutes: arrivalDelta !== null ? Math.round(arrivalDelta / 60000) : null,
      totalBookingMinutes: arrivedDate && finalizedDate ? Math.round((finalizedDate - arrivedDate) / 60000) : null
    });
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content dark" onClick={e => e.stopPropagation()}>
        <h2>Detalhes do Agendamento</h2>
        <div className="booking-details">
          <div className="form-group">
            <label>Paciente</label>
            <div className="detail-value">{booking.paciente}</div>
          </div>

          <div className="form-group">
            <label>Data e Hora</label>
            <div className="detail-value">
              {scheduledDisplay.replace('T', ' ').replace('Z', '')}
            </div>
          </div>

          <div className="form-group">
            <label>Duração (minutos)</label>
            <div className="detail-value">{booking.duration}</div>
          </div>

          <div className="form-group">
            <label>Procedimentos</label>
            <div className="detail-value">
              {booking.procedimentos?.map((proc, i) => (
                <div key={i}>{proc.nome}</div>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Valor Total</label>
            <div className="detail-value editable">
              <input
                type="text"
                inputMode="decimal"
                value={editedValorTotal}
                onChange={(e) => setEditedValorTotal(e.target.value)}
                className="commission-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Comissão</label>
            <div className="detail-value editable">
              <input
                type="text"
                inputMode="decimal"
                value={editedComission}
                onChange={(e) => setEditedComission(e.target.value)}
                className="commission-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Status do Atendimento</label>
            <div className="detail-value">{booking.status || 'Agendado'}</div>
          </div>

          {arrivedTimestamp && (
            <div className="form-group">
              <label>Paciente Chegou</label>
              <div className="detail-value">{arrivedTimestamp}</div>
            </div>
          )}

          {arrivalDeltaText && (
            <div className="form-group">
              <label>{arrivalDeltaLabel}</label>
              <div className="detail-value">{arrivalDeltaText}</div>
            </div>
          )}

          {finalizedTimestamp && (
            <div className="form-group">
              <label>Atendimento Finalizado</label>
              <div className="detail-value">{finalizedTimestamp}</div>
            </div>
          )}

          {totalBookingTime && (
            <div className="form-group">
              <label>Tempo Total do Atendimento</label>
              <div className="detail-value">{totalBookingTime}</div>
            </div>
          )}

          <div className="form-group">
            <label>Forma de Pagamento</label>
            <div className="detail-value">{booking.paymentMethod || 'Não definido'}</div>
          </div>

          <div className="form-group">
            <label>Status do Pagamento</label>
            <div className="detail-value">{booking.paymentStatus || 'Pendente'}</div>
          </div>

          {booking.paymentDate && (
            <div className="form-group">
              <label>Data do Pagamento</label>
              <div className="detail-value">
                {booking.paymentDate.replace('T', ' ').replace('Z', '')}
              </div>
            </div>
          )}

          {booking.observacao && (
            <div className="form-group">
              <label>Observação</label>
              <div className="detail-value">{booking.observacao}</div>
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button 
            className="action-button update" 
            onClick={handleUpdateComission}
            disabled={isUpdating}
          >
            {isUpdating ? 'Atualizando...' : 'Atualizar Comissão'}
          </button>
          {booking.file_key && (
            <button 
              className="action-button" 
              onClick={handleDownloadReceipt}
              disabled={isDownloading}
            >
              {isDownloading ? 'Baixando...' : 'Baixar Comprovante'}
            </button>
          )}
          <button className="action-button" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

export default ViewBookingModal; 
